import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import financeApiService from "../../../../services/financeApiService";
import { messageErreurDepot } from "../../../../helpers/depotTools";
import tools from "../../../../helpers/tools";
import styles from "../FinanceDetailV2.module.scss";

export const TYPE_TABLEAU_AMORTISSEMENT_SOURCE = "tableau_amortissement_source";

const MIME_ICONS = {
  "application/pdf": "file-pdf-o",
  "image/jpeg": "file-image-o",
  "image/png": "file-image-o",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
    "file-excel-o",
  "application/vnd.ms-excel": "file-excel-o",
};

const DEFAULT_DEVIS_LINKED_TYPE_OPTIONS = [
  { value: TYPE_TABLEAU_AMORTISSEMENT_SOURCE, label: "Calendrier des loyers" },
];

/**
 * Documents rattachés à un devis précis de la demande, listés dans l'onglet du
 * devis concerné. Le dépôt reste spécifique au calendrier des loyers (Excel,
 * LS-634) ; les autres types rattachés à un devis (CPI, LS-638) se déposent
 * depuis la modale générique (DocumentsUploader) mais sont affichés ici aussi
 * — la liste des types à agréger (`devisLinkedTypeOptions`, ceux dont
 * `requiertUnDevis` est vrai côté API) vient du parent, jamais hardcodée.
 *
 * LS-638 — un même fichier calendrier des loyers peut aussi couvrir d'autres
 * devis de la demande (`devisOptions`, financeur envoyant un échéancier
 * groupé) : ils sont proposés en cases à cocher, en plus du devis courant qui
 * reste implicite.
 *
 * ST1-2069 — ces documents sont aussi listés dans la section "Documents"
 * générique (DocumentsUploader) : cette liste n'est plus leur seul point de
 * consultation. Chaque fichier est préfixé par le libellé de son type, comme
 * là-bas, pour qu'un CPI ne soit pas pris pour un calendrier des loyers sous
 * le bouton de dépôt. `reloadKey` force un rechargement quand la section
 * "Documents" a déposé ou supprimé un document rattaché à ce devis.
 */
const EcheancierUploader = ({
  demandeUuid,
  devisUuid,
  disabled = false,
  onUploaded,
  devisOptions = [],
  devisLinkedTypeOptions = DEFAULT_DEVIS_LINKED_TYPE_OPTIONS,
  reloadKey = 0,
}) => {
  const [documents, setDocuments] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [devisAdditionnels, setDevisAdditionnels] = useState([]);

  const autresDevis = devisOptions.filter((opt) => opt.value !== devisUuid);

  useEffect(() => {
    if (!demandeUuid || !devisUuid) return;
    setIsLoading(true);
    Promise.all(
      devisLinkedTypeOptions.map((type) =>
        financeApiService
          .getDocuments(demandeUuid, type.value)
          .then((res) =>
            (res.data || [])
              .filter((doc) => doc.devisUuid === devisUuid)
              .map((doc) => ({ ...doc, typeLabel: type.label })),
          ),
      ),
    )
      .then((parType) => setDocuments(parType.flat()))
      .catch(() =>
        toast.error("Impossible de charger les documents de ce devis."),
      )
      .finally(() => setIsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [demandeUuid, devisUuid, reloadKey]);

  function toggleDevisAdditionnel(uuid) {
    setDevisAdditionnels((prev) =>
      prev.includes(uuid)
        ? prev.filter((u) => u !== uuid)
        : [...prev, uuid],
    );
  }

  async function handleFileChange(e) {
    const file = e.target.files?.[0];
    e.target.value = ""; // permet de re-sélectionner le même fichier
    if (!file) return;
    setIsUploading(true);
    try {
      const cibles = [devisUuid, ...devisAdditionnels];
      const uploaded = [];
      for (const cible of cibles) {
        const res = await financeApiService.uploaderDocument(
          demandeUuid,
          file,
          TYPE_TABLEAU_AMORTISSEMENT_SOURCE,
          cible,
        );
        uploaded.push(res.data);
      }
      // Seul le document rattaché au devis courant appartient à cette liste —
      // ceux déposés sur d'autres devis (devisAdditionnels) sont affichés par
      // leur propre onglet, rechargé au prochain montage.
      setDocuments((prev) => [
        ...prev,
        ...uploaded
          .filter((doc) => doc.devisUuid === devisUuid)
          .map((doc) => ({ ...doc, typeLabel: "Calendrier des loyers" })),
      ]);
      toast.success(
        uploaded.length > 1
          ? "Calendrier des loyers déposé sur les devis sélectionnés."
          : "Calendrier des loyers déposé.",
      );
      setDevisAdditionnels([]);
      if (onUploaded) onUploaded();
    } catch (err) {
      toast.error(
        messageErreurDepot(err, {
          interdit: "Vous n'êtes pas autorisé à déposer ce document.",
          defaut: "L'upload du calendrier des loyers a échoué.",
        }),
      );
    } finally {
      setIsUploading(false);
    }
  }

  if (!demandeUuid || !devisUuid) return null;

  return (
    <div className={styles.echeancierUploader}>
      {isLoading ? (
        <span className={styles.financierCardStatusMuted}>Chargement…</span>
      ) : (
        documents.length > 0 && (
          <ul className={styles.documentsList}>
            {documents.map((doc) => (
              <li key={doc.uuid} className={styles.documentItem}>
                <i
                  className={`fa fa-${MIME_ICONS[doc.mimeType] || "file-o"} ${styles.documentIcon}`}
                />
                <span className={styles.documentBody}>
                  <a
                    href={doc.url}
                    target="_blank"
                    rel="noreferrer"
                    className={styles.documentName}
                  >
                    {doc.typeLabel ? `${doc.typeLabel} — ${doc.fileName}` : doc.fileName}
                  </a>
                  <span className={styles.documentMeta}>
                    {doc.createdAt ? tools.formatDate(doc.createdAt) : ""}
                  </span>
                </span>
              </li>
            ))}
          </ul>
        )
      )}
      {!disabled && autresDevis.length > 0 && (
        <div className={styles.devisCheckboxList} data-testid="devis-additionnels">
          {autresDevis.map((opt) => (
            <label key={opt.value} className={styles.devisCheckboxItem}>
              <input
                type="checkbox"
                checked={devisAdditionnels.includes(opt.value)}
                onChange={() => toggleDevisAdditionnel(opt.value)}
              />
              Aussi sur {opt.label}
            </label>
          ))}
        </div>
      )}
      {!disabled && (
        <label
          className={`${styles.echeancierToggleBtn} ${styles.echeancierUploadLabel}`}
        >
          <i
            className={`fa fa-${isUploading ? "spinner fa-spin" : "upload"}`}
            style={{ marginRight: 4 }}
          />
          {isUploading ? "Envoi..." : "Déposer le calendrier des loyers"}
          <input
            type="file"
            accept=".xlsx,.xls"
            onChange={handleFileChange}
            disabled={isUploading}
            hidden
          />
        </label>
      )}
    </div>
  );
};

export default EcheancierUploader;
